import { useMutation } from "@apollo/client";
import {
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { LinearTagChip } from "components/LinearTagChip";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { EntityUpdate } from "components/ManageTags/types";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import {
  FilterGroup,
  parseOmnisearch,
  stringifyOmnisearch,
} from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLinearTags } from "hooks/useLinearTags";
import { OmnisearchGridColDef, useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useSuspenseQueryParams } from "hooks/useSuspenseQueryParams";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { prettyPrintDate } from "util/dates";

import {
  ActivityStatus,
  ActivityType,
  ExecutionPlanType,
  JobStatus,
  TierIiReportKind,
  WorkflowActivitiesQuery,
  WorkflowActivitiesQueryVariables,
  WorkflowStatus,
  WorkflowType,
} from "../../../../generated-graphql/graphql";
import { ExportGridToCsvButton } from "../AssignmentManagerTable/ExportGridToCsvButton";
import { FULFILLMENT_DEFAULT_REPORT_KIND } from "../fulfillmentDefaults";
import { useStats } from "../StatsContext";
import { WorkflowOverviewStats } from "../WorkflowOverviewStats/WorkflowOverviewStats";
import { WORKFLOW_ACTIVITIES_QUERY } from "./api";
import { BulkActionsMenu } from "./BulkActionsMenu";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "./ManageWorkflowTags";
import { WorkflowActivitiesRow } from "./types";

const persistenceKey = "workflow-activities";

export function WorkflowActivitiesTable() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const theme = useTheme();
  const { statsOpen } = useStats();
  const omnisearchInputRef = useRef<HTMLInputElement>(null);

  const { omnisearch, sortModel, setOmnisearch, getOmnisearchValue } = useOmnisearchDatagrid({
    persistenceKey,
    isURLDriven: true,
  });

  const [selectedRows, setSelectedRows] = useState<WorkflowActivitiesRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<Array<WorkflowActivitiesRow["id"]>>([]);
  const {
    optimisticFilters: { includeAllStaffActivities = false },
    setFilters,
  } = useSuspenseQueryParams<{ includeAllStaffActivities: boolean }>();

  const setIncludeAllStaffActivities = useCallback(
    (checked: boolean) => {
      setFilters((draft) => {
        draft.includeAllStaffActivities = checked;
      });
    },
    [setFilters]
  );

  // State for single-row tag management dialog
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [selectedRowForTags, setSelectedRowForTags] = useState<WorkflowActivitiesRow | null>(null);
  const [showTagsConfirmation, setShowTagsConfirmation] = useState(false);
  const alerts = useAlerts();

  // Mutation for updating workflow tags
  const [updateWorkflowTags, { loading: updateTagsLoading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: ["WorkflowActivities"],
    }
  );

  // Handle opening tags dialog for a single row
  const handleOpenTagsDialog = useCallback((row: WorkflowActivitiesRow) => {
    setSelectedRowForTags(row);
    setTagsDialogOpen(true);
  }, []);

  // Handle closing tags dialog
  const handleCloseTagsDialog = useCallback(() => {
    setTagsDialogOpen(false);
    setShowTagsConfirmation(false);
    setSelectedRowForTags(null);
  }, []);

  // Handle submitting tag updates
  const handleSubmitTags = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        const workflowCount = workflowUpdates.length;
        alerts.success(
          `Successfully updated tags for ${workflowCount} ${pluralize("workflow", workflowCount)}`
        );

        handleCloseTagsDialog();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, handleCloseTagsDialog]
  );

  // Transform selected row to dialog format
  const tagsDialogRows = useMemo(() => {
    if (!selectedRowForTags || !selectedRowForTags.workflowId) return [];
    return [
      {
        id: selectedRowForTags.workflowId,
        tags: selectedRowForTags.tags || [],
      },
    ];
  }, [selectedRowForTags]);

  // Capture rows from the datagrid's own query so we can collect Linear tags
  // without firing a second WorkflowActivities request.
  const [gridData, setGridData] = useState<WorkflowActivitiesQuery | undefined>(undefined);

  const allTagsForLinear = useMemo(() => {
    const tagSet = new Set<string>();
    const items = gridData?.workflowActivities?.items ?? [];
    items.forEach((row) => {
      row.tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet);
  }, [gridData]);

  // Get Linear issue statuses for all tags
  const { statusMap: linearStatusMap } = useLinearTags(allTagsForLinear);

  // Track omnisearch changes to handle scrolling from UnverifiedTable
  const prevOmnisearch = useRef<string>(omnisearch);

  // Scroll to omnisearch when navigation from UnverifiedTable is detected
  useEffect(() => {
    if (prevOmnisearch.current !== omnisearch && omnisearch) {
      // Check for UnverifiedTable navigation (State/Organization + Unverified)
      const hasUnverifiedFilter =
        omnisearch.includes("Unverified:true") || omnisearch.toLowerCase().includes("unverified");
      const hasStateOrOrgFilter =
        omnisearch.includes("State:") ||
        omnisearch.includes("Organization:") ||
        omnisearch.toLowerCase().includes("state:") ||
        omnisearch.toLowerCase().includes("organization:");

      // If navigation from UnverifiedTable is detected
      if (hasUnverifiedFilter && hasStateOrOrgFilter) {
        if (omnisearchInputRef.current) {
          // Scroll to omnisearch input and focus it
          setTimeout(() => {
            if (omnisearchInputRef.current) {
              // Get the element's position
              const rect = omnisearchInputRef.current.getBoundingClientRect();
              const headerHeight = 80; // Approximate sticky header height

              // Scroll to position that accounts for the sticky header
              window.scrollTo({
                top: window.pageYOffset + rect.top - headerHeight,
                behavior: "smooth",
              });

              // Focus the search input so user can immediately start typing
              omnisearchInputRef.current.focus();
            }
          }, 300);
        }
      }
    }
    prevOmnisearch.current = omnisearch;
  }, [omnisearch]);

  const handleSelectedRowsChanged = useCallback((rows: WorkflowActivitiesRow[]) => {
    setSelectedRows(rows);
    setSelectedRowIds(rows.map((row) => row.id));
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRows([]);
    setSelectedRowIds([]);
  }, []);

  // Check if "Assigned to me" filter is currently active
  const isAssignedToMeActive = useMemo(() => {
    if (!user?.email) return false;
    const filters = parseOmnisearch(omnisearch);
    return filters.assigneeEmail?.includes(user.email);
  }, [omnisearch, user?.email]);

  // Handle "Assigned to me" toggle
  const handleAssignedToMeToggle = useCallback(
    (checked: boolean) => {
      if (!user?.email) return;

      const currentFilters = parseOmnisearch(omnisearch);
      const reportKind = getOmnisearchValue("reportKind");

      if (checked) {
        // Add assigneeEmail filter
        const newFilters = {
          ...currentFilters,
          reportKind,
          assigneeEmail: user.email,
        };
        setOmnisearch(stringifyOmnisearch(newFilters));
      } else {
        // Remove assigneeEmail filter
        const { assigneeEmail, ...newFilters } = currentFilters;

        setOmnisearch(
          stringifyOmnisearch({
            ...newFilters,
            reportKind,
          })
        );
      }
    },
    [omnisearch, setOmnisearch, user?.email, getOmnisearchValue]
  );

  const reportingYearRaw = getOmnisearchValue("reportingYear");
  const reportKindRaw = getOmnisearchValue("reportKind");
  const defaultSearch = useMemo(() => {
    const search = [];

    if (!reportingYearRaw) {
      search.push(`reportingYear:${currentTierIIReportingYear}`);
    }

    if (!reportKindRaw) {
      search.push(`reportKind:${FULFILLMENT_DEFAULT_REPORT_KIND}`);
    }

    return search.length > 0 ? search.join(" ") : undefined;
  }, [reportingYearRaw, reportKindRaw]);

  const columns: OmnisearchGridColDef<WorkflowActivitiesRow>[] = useMemo(
    () => [
      // === Organization/Facility columns ===
      {
        field: "organizationName",
        headerName: "Organization",
        flex: 0.2,
        minWidth: 120,
        filterable: false,
      },
      {
        field: "partnerTenantName",
        headerName: "Partner Tenant",
        flex: 1,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        renderCell: ({ row }) => {
          if (row.cohortId == null) {
            return (
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                <Typography variant="body2">{row.name}</Typography>
                <Typography variant="caption">{row.facilityAddress}</Typography>
              </Box>
            );
          }
          return <Typography variant="body2">{row.name}</Typography>;
        },
      },
      {
        field: "state",
        headerName: "State/Territory",
        flex: 0.08,
        minWidth: 50,
      },
      {
        field: "stateEnabled",
        headerName: "State Enabled",
        flex: 0.8,
        filterKeyType: "boolean",
        filterable: true,
        renderCell: ({ row }) => {
          return row.stateEnabled ? (
            <Typography variant="body2" color="success">
              Yes
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No
            </Typography>
          );
        },
      },
      {
        field: "county",
        headerName: "County",
        flex: 0.15,
        minWidth: 100,
        filterKeyType: "text",
      },
      {
        field: "chemicalCount",
        headerName: "Chemicals",
        flex: 0.08,
        minWidth: 60,
        type: "number",
        sortingOrder: ["desc", "asc"],
        renderCell: (params) => {
          return params.value || params.value === 0 ? params.value : "-";
        },
      },

      // === Workflow columns ===
      {
        field: "workflowType",
        headerName: "Workflow Type",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(WorkflowType),
        renderCell: ({ row }) => {
          return prettyPrintEnumValue(row.workflowType);
        },
      },
      {
        field: "reportingYear",
        headerName: "Reporting Year",
        flex: 0.8,
      },
      {
        field: "reportKind",
        headerName: "Report Kind",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(TierIiReportKind),
        renderCell: ({ row }) => {
          const reportKindValue =
            row.reportKind ?? (row.cohortId != null ? TierIiReportKind.Annual : null);

          if (reportKindValue == null) {
            return "";
          }

          return <Typography variant="body2">{prettyPrintEnumValue(reportKindValue)}</Typography>;
        },
      },
      {
        field: "workflowStatus",
        headerName: "Workflow Status",
        flex: 1,
        enumValues: Object.values(WorkflowStatus),
        filterable: true,
        filterKeyType: "enumAutocomplete",
        renderCell: ({ row }) => {
          if (row.workflowStatus) {
            return (
              <Typography variant="body2">{prettyPrintEnumValue(row.workflowStatus)}</Typography>
            );
          }
          return "";
        },
      },

      // === Assignee column ===
      {
        field: "assigneeEmail",
        headerName: "Assignee Email",
        flex: 1.2,
        nullCheckboxLabel: "Unassigned only",
        renderCell: ({ row }) => {
          if (row.assigneeEmail === "Unassigned") {
            return <Typography variant="body2"></Typography>;
          } else {
            return <Typography variant="body2">{row.assigneeEmail}</Typography>;
          }
        },
      },

      // === Activity columns (grouped) ===
      {
        field: "activityType",
        headerName: "Activity Type",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ActivityType),
      },
      {
        field: "jurisdictionType",
        headerName: "Jurisdiction Type",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: ["State", "City/County"],
        renderCell: ({ row }) => {
          if (!row.jurisdictionType) return null;
          return <Typography variant="body2">{row.jurisdictionType}</Typography>;
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.5,
      },
      {
        field: "activityStatus",
        headerName: "Activity Status",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ActivityStatus),
        renderCell: ({ row }) => {
          if (row.activityType) {
            if (row.activityStatus === ActivityStatus.Completed)
              return (
                <Typography variant="body2" color="success">
                  Completed
                </Typography>
              );
            else if (row.activityStatus === ActivityStatus.Error)
              return (
                <Typography variant="body2" color="error">
                  Error
                </Typography>
              );
            else return prettyPrintEnumValue(row.activityStatus);
          }
          return "N/A";
        },
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1.5,
      },
      {
        field: "activityCreatedAt",
        headerName: "Activity Created At",
        flex: 1,
        filterKeyType: "date",
        renderCell: ({ row }) => {
          if (row.activityCreatedAt) {
            return (
              <Typography variant="body2">{prettyPrintDate(row.activityCreatedAt)}</Typography>
            );
          }
          return "";
        },
      },
      {
        field: "activityUpdatedAt",
        headerName: "Activity Updated At",
        flex: 1,
        filterKeyType: "date",
        renderCell: ({ row }) => {
          if (row.activityUpdatedAt) {
            return (
              <Typography variant="body2">{prettyPrintDate(row.activityUpdatedAt)}</Typography>
            );
          }
          return "";
        },
      },
      {
        field: "plan",
        headerName: "Plan",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ExecutionPlanType),
      },
      {
        field: "jobStatus",
        headerName: "Job Status",
        flex: 0.8,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobStatus),
        renderCell: ({ row }) => {
          if (row.jobStatus?.includes("FAILED"))
            return (
              <Typography variant="body2" color="error">
                {prettyPrintEnumValue(row.jobStatus)}
              </Typography>
            );
          else if (row.jobStatus === JobStatus.Succeeded)
            return (
              <Typography variant="body2" color="success">
                Succeeded
              </Typography>
            );
          else return prettyPrintEnumValue(row.jobStatus);
        },
      },

      // === Other columns ===
      {
        field: "fulfillmentNote",
        headerName: "Fulfillment Note",
        flex: 0.2,
        minWidth: 150,
        sortable: false,
        filterable: true,
        renderCell: ({ row }) => {
          return row.fulfillmentNote ? (
            <Tooltip
              title={row.fulfillmentNote}
              slotProps={{
                tooltip: { sx: { whiteSpace: "pre-wrap" } },
              }}
            >
              <Typography variant="body2" noWrap>
                {row.fulfillmentNote}
              </Typography>
            </Tooltip>
          ) : null;
        },
      },

      // === Assignment columns ===
      {
        field: "credentials",
        headerName: "Credentials",
        flex: 1,
        renderCell: ({ row }) => {
          if (!row.credentials || row.credentials.length === 0) return null;

          // For now, just show the first credential
          const credential = row.credentials[0];

          return (
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="body2">{credential.username}</Typography>
              <Typography variant="caption">
                {prettyPrintEnumValue(credential.verificationStatus)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "tags",
        headerName: "Tags",
        filterKeyType: "workflowTags",
        flex: 1,
        linkable: false, // Prevent row link wrapper - tags have their own click handlers
        renderCell: ({ row }) => {
          if (row.tags.length === 0) return null;

          // Prefer showing a Linear ticket tag (ENG-####) if present; otherwise show the first tags.
          // Keep the compact two-row UI, but don't hide Linear tags behind "+N more".
          const linearTags = row.tags.filter((t) => /^ENG-\d+$/i.test(t));
          const nonLinearTags = row.tags.filter((t) => !/^ENG-\d+$/i.test(t));
          const orderedTags = [...linearTags, ...nonLinearTags];

          const visibleTags = orderedTags.slice(0, 2);
          const overflowCount = orderedTags.length - visibleTags.length;
          const showSecondRow = visibleTags.length >= 2;

          return (
            <Box
              sx={{ py: 0.25, cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenTagsDialog(row);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.25,
                  mb: showSecondRow ? 0.25 : 0,
                }}
              >
                <LinearTagChip
                  tag={visibleTags[0]}
                  linearStatusMap={linearStatusMap}
                  size="small"
                  sx={{ height: 18, fontSize: "0.65rem" }}
                  onNormalClick={() => handleOpenTagsDialog(row)}
                />
              </Box>
              {showSecondRow && (
                <Box sx={{ display: "flex", gap: 0.25, alignItems: "center" }}>
                  {overflowCount === 0 ? (
                    <LinearTagChip
                      tag={visibleTags[1]}
                      linearStatusMap={linearStatusMap}
                      size="small"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                      onNormalClick={() => handleOpenTagsDialog(row)}
                    />
                  ) : (
                    <>
                      <LinearTagChip
                        tag={visibleTags[1]}
                        linearStatusMap={linearStatusMap}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                        onNormalClick={() => handleOpenTagsDialog(row)}
                      />
                      <Chip
                        label={`+${overflowCount} more`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        },
      },

      // === Account team columns ===
      {
        field: "csmName",
        headerName: "CSM",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.csmName && !row.csmEmail) return null;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="body2">{row.csmName}</Typography>
              <Typography variant="caption">{row.csmEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "amName",
        headerName: "Account Manager",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.amName && !row.amEmail) return null;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="body2">{row.amName}</Typography>
              <Typography variant="caption">{row.amEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "dataManagerName",
        headerName: "Data Manager",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.dataManagerName && !row.dataManagerEmail) return null;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="body2">{row.dataManagerName}</Typography>
              <Typography variant="caption">{row.dataManagerEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "wasteManagerName",
        headerName: "Waste Manager",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.wasteManagerName && !row.wasteManagerEmail) return null;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="body2">{row.wasteManagerName}</Typography>
              <Typography variant="caption">{row.wasteManagerEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "pocName",
        headerName: "Customer POC",
        flex: 1,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: ({ row }) => {
          if (!row.pocName && !row.pocEmail) return null;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="body2">{row.pocName}</Typography>
              <Typography variant="caption">{row.pocEmail}</Typography>
            </Box>
          );
        },
      },

      // === Cohort columns ===
      {
        field: "cohortMemberCount",
        headerName: "Cohort Member Count",
        flex: 1,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.workflowType !== WorkflowType.Cohort) return <></>;

          return (
            <Tooltip
              title={`${row.cohortReadyMemberCount} cohort ${
                row.cohortReadyMemberCount === 1 ? "member" : "members"
              } ready out of ${row.cohortTotalMemberCount} total possible members.`}
              placement="top"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                {row.cohortReadyMemberCount} / {row.cohortTotalMemberCount}
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [linearStatusMap, handleOpenTagsDialog]
  );

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      // Column 1: Organization + Workflow
      {
        name: "🏢 Organization & Facility",
        fields: ["tenant", "partnerTenantName", "name", "state", "stateEnabled", "county"],
      },
      {
        name: "⚙️ Workflow & Activity",
        fields: [
          "activityType",
          "jurisdictionType",
          "activityStatus",
          "workflowType",
          "workflowStatus",
          "plan",
          "jobStatus",
        ],
      },
      // Column 2: Report + Tags + Account Team
      {
        name: "📋 Report",
        fields: ["reportingYear", "reportKind"],
      },
      {
        name: "🏷️ Tags & Assignment",
        fields: ["tags", "assigneeEmail", "credentials"],
      },
      {
        name: "👥 Account Team",
        fields: ["csmName", "amName", "dataManagerName", "wasteManagerName"],
      },
      // Column 3: Customer (Other Filters will follow)
      {
        name: "🤝 Customer",
        fields: ["pocName"],
      },
    ],
    []
  );

  return (
    <Box p={theme.spacing(2)}>
      <Collapse in={statsOpen}>
        <WorkflowOverviewStats />
      </Collapse>
      <Paper elevation={0} sx={{ display: "flex", flexDirection: "column", position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          px={3}
          paddingTop={3}
          paddingBottom={theme.spacing(3)}
        >
          <Typography variant="body1" color={theme.palette.text.secondary}>
            {includeAllStaffActivities ? "Current and Staff Activities" : "Current Activities"}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={includeAllStaffActivities}
                  onChange={(_, checked) => setIncludeAllStaffActivities(checked)}
                />
              }
              label="Include all staff activities"
            />
            <FormControlLabel
              sx={{ marginRight: "0px" }}
              control={
                <Switch
                  checked={isAssignedToMeActive}
                  onChange={(_, checked) => handleAssignedToMeToggle(checked)}
                  disabled={!user?.email}
                />
              }
              label="Assigned to me"
            />
          </Stack>
        </Box>

        <Box position="relative" pb={3} sx={{ height: "100vh" }}>
          <OmnisearchDataGrid<
            WorkflowActivitiesQuery,
            WorkflowActivitiesQueryVariables,
            WorkflowActivitiesRow
          >
            omnisearchInputRef={omnisearchInputRef}
            autoFocus={false}
            isURLDriven
            persistenceKey={persistenceKey}
            columns={columns}
            columnVisibilityModel={{
              stateEnabled: false,
              fulfillmentNote: false,
              jurisdictionType: false,
              csmName: false,
              amName: false,
              dataManagerName: false,
              wasteManagerName: false,
              pocName: false,
            }}
            dataQuery={WORKFLOW_ACTIVITIES_QUERY}
            dataQueryVariables={{ includeAllStaffActivities }}
            defaultSearch={defaultSearch}
            onDataStateChange={setGridData}
            additionalFilterColumns={[
              {
                header: "Organization",
                key: "tenant",
                filterKeyType: "multipleTenant",
              },
            ]}
            filterGroups={filterGroups}
            wideLayout
            checkboxSelection
            disableRowSelectionOnClick
            filterSelectionToVisibleItems
            getItems={(data) => data.workflowActivities.items}
            getCount={(data) => data.workflowActivities.count}
            onRowClick={({ row }, event) => {
              // Check if click originated from tags column - skip navigation if so
              const target = event.target as HTMLElement;
              const tagsCell = target.closest('[data-field="tags"]');
              if (tagsCell) {
                return; // Let the tag click handler manage this
              }

              if (row.tierIIReportId)
                navigate(`/staff/fulfillment/${row.tierIIReportId}/activities/${row.activityId}`);
              else if (row.cohortId) navigate(`/staff/cohorts/${row.cohortId}`);
            }}
            getRowLink={(row) => {
              if (row.tierIIReportId)
                return `/staff/fulfillment/r/${row.tierIIReportId}/activities/${row.activityId}`;
              else if (row.cohortId) return `/staff/cohorts/${row.cohortId}`;

              return null;
            }}
            onSelectedRowsChanged={handleSelectedRowsChanged}
            selectedIds={selectedRowIds}
            commandButtons={[
              <Stack
                key="command-buttons"
                id="command-buttons"
                sx={{
                  marginBottom: "1rem",
                  marginTop: "1rem",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                }}
                spacing={2}
                alignItems="center"
              >
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  marginTop={0}
                  sx={{ flex: { sm: 1, lg: 2 }, alignItems: "center" }}
                >
                  <Box sx={{ marginRight: 2 }}>
                    <ExportGridToCsvButton
                      search={omnisearch}
                      sort={[...sortModel]}
                      grid="workflowActivities"
                    />
                  </Box>
                  <BulkActionsMenu
                    selectedRows={selectedRows}
                    onClearSelection={handleClearSelection}
                  />
                </Stack>
              </Stack>,
            ]}
            gridSx={{
              height: "100%",
              minHeight: "400px",
              maxHeight: "calc(100vh - 280px)", // Account for search controls, filters, and padding
            }}
          />
        </Box>
      </Paper>

      {/* Manage Tags Dialog for single-row tag editing */}
      <ManageTagsFormDialog
        selectedRows={tagsDialogRows}
        getEntityId={(row) => row.id}
        onClose={handleCloseTagsDialog}
        onCloseConfirmation={() => setShowTagsConfirmation(false)}
        confirm={() => setShowTagsConfirmation(true)}
        open={tagsDialogOpen}
        withConfirmation={true}
        showConfirmation={showTagsConfirmation}
        onSubmit={handleSubmitTags}
        loading={updateTagsLoading}
        entityName="Workflow"
        localStorageKey="workflow-activities-recent-tags"
        commonTags={["compliance", "engineering", "linear"]}
        TagPickerComponent={WorkflowTagPickerAdapter}
      />
    </Box>
  );
}
